// BEAM UI primitive — Screen.
// Every screen sits on paper (color.bg.app), inside the safe area, with the standard
// horizontal gutter (layout.screenPadding). Consumes only theme tokens.
import React from 'react';
import {
  ScrollView,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { SafeAreaView, type Edge } from 'react-native-safe-area-context';
import { theme } from '../../theme';

type ScreenProps = {
  children: React.ReactNode;
  /** Wrap content in a ScrollView (default false). */
  scroll?: boolean;
  /** Apply the horizontal screen gutter (default true). */
  padded?: boolean;
  /** Safe-area edges to inset (default top + bottom). */
  edges?: readonly Edge[];
  /**
   * W5 — cap + center content width on wide (web) viewports. Defaults to theme.layout.contentMaxWidth.
   * Pass null to opt out (full-bleed, e.g. for a screen that manages its own master-detail columns).
   * No effect on phones (their width is already below the cap).
   */
  maxWidth?: number | null;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
};

export function Screen({
  children,
  scroll = false,
  padded = true,
  edges = ['top', 'bottom'],
  maxWidth,
  style,
  contentContainerStyle,
}: ScreenProps) {
  const pad = padded ? { paddingHorizontal: theme.layout.screenPadding } : null;
  // Center content at the cap on wide viewports. `undefined` → default cap; `null` → no cap.
  const cap = maxWidth === null ? null : (maxWidth ?? theme.layout.contentMaxWidth);
  const centered: ViewStyle | null = cap ? { width: '100%', maxWidth: cap, alignSelf: 'center' } : null;

  if (scroll) {
    return (
      <SafeAreaView style={[styles.root, style]} edges={edges}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[styles.scrollContent, pad, centered, contentContainerStyle]}
          keyboardShouldPersistTaps="handled"
        >
          {children}
        </ScrollView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.root, style]} edges={edges}>
      <View style={[styles.flex, pad, centered, contentContainerStyle]}>{children}</View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.color.bg.app },
  flex: { flex: 1 },
  scrollContent: { paddingVertical: theme.spacing.xl, gap: theme.spacing.lg, flexGrow: 1 },
});
